/**
 * useFeature — lightweight feature-gate hook backed by localStorage.
 *
 * The enabled-features list is written to localStorage under "lyra_features"
 * whenever onboarding completes or the app loads an authenticated session.
 * Features are slug strings like "github-skill", "telegram-agent",
 * or "experience:advanced".
 *
 * Usage:
 *   const hasGithub  = useFeature("github-skill");
 *   const isAdvanced = useFeature("experience:advanced");
 */

const STORAGE_KEY = 'lyra_features';

/** Write the features array to localStorage. Called by App.tsx after fetch. */
export function setStoredFeatures(features: string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(features));
  } catch (e) { /* storage quota edge case — ignore */ }
}

/** Read the features array from localStorage. */
export function getStoredFeatures(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as string[];
  } catch (e) {
    return [];
  }
}

/** Clear stored features (call on logout). */
export function clearStoredFeatures(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Hook: returns true if `slug` is in the user's enabled-features list.
 * Re-evaluates only on mount (features are stable after login).
 */
export function useFeature(slug: string): boolean {
  return getStoredFeatures().includes(slug);
}
